/**
 * @file 文档搜索 hook
 * @author FengGuang(fengguang01@baidu.com)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IPagination, ISearchItem, ISearchTypeItem, ISearchTypeList } from './type';
import { fetchGetSearchList } from '../../api/pages/searchAllApi';
import filteHtml from '../../utils/filte-html';
import { fetchPostSearchInfo } from '../../api/components/headerApi';
import { getScrollTop } from '../../utils/get-scroll';

const useSearchAllRouterStore = () => {
    const location = useLocation();
    const history = useHistory();
    const [keyword, setKeyword] = useState<string>(() => {
        const searchParam = new URLSearchParams(location.search.slice(1));
        return searchParam.get('q') || '';
    });
    const [searchResultList, setSearchResultList] = useState<ISearchItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState<IPagination>({
        current: 1,
        total: 0,
        pageSize: 10
    });

    const searchType = useMemo(() => {
        const searchParam = new URLSearchParams(location.search.slice(1));
        return searchParam.get('searchType') || 'all';
    }, [location.search]);

    const historyPush = history.push;
    const onSearchTypeChange = useCallback(
        (activeKey: string) => {
            const searchParams = new URLSearchParams(location.search.slice(1));
            searchParams.delete('page');
            searchParams.set('searchType', `${activeKey}`);
            historyPush(`?${searchParams.toString()}`);
        },
        [historyPush, location.search]
    );

    const [searchTypeList, setSearchTypeList] = useState<ISearchTypeList>([]);

    // 这个接口请求速度慢，使用时间戳来保证显示的结果是最后一次请求
    const timeStampRef = useRef<number>(0);

    useEffect(() => {
        (async (): Promise<void> => {
            setLoading(true);

            const theTimeStamp = Date.now();
            timeStampRef.current = theTimeStamp;

            const res = await fetchGetSearchList().catch(err => {
                // ignore
            });

            setLoading(false);

            if (res && timeStampRef.current === theTimeStamp) {
                const resBody = res.body ?? {};
                const pageData = resBody.pageData || {};
                const list: any[] = Array.isArray(pageData.data) ? pageData.data : [];

                const resList: ISearchItem[] = list.map(item => ({
                    title: item.title,
                    path: item.path,
                    content: (item.content || '').replace(/¶/g, '')
                }));
                setSearchResultList(resList);
                setPagination(oldPagination => ({
                    ...oldPagination,
                    current: pageData.page,
                    total: pageData.totalCount,
                    pageSize: pageData.pageSize
                }));

                const searchTypeList: ISearchTypeList = (() => {
                    const sc: any[] = pageData.searchCount || [];

                    const list = sc.map(item => ({
                        key: item.searchType,
                        value: item.searchType,
                        label: `${item.searchTypeName}(${item.count})`
                    }));

                    const searchTypeSortKey = ['all', 'api', 'doc', 'tutorial', 'product', 'calendar'];
                    const searchTypeListMap = new Map<string, ISearchTypeItem>(list.map(i => [i.value, i]));

                    let searchTypeSort: ISearchTypeList = [];
                    searchTypeSortKey.forEach(key => {
                        const item = searchTypeListMap.get(key);
                        if (item) {
                            searchTypeSort.push(item);
                            searchTypeListMap.delete(key);
                        }
                    });
                    searchTypeSort = [...searchTypeSort, ...Array.from(searchTypeListMap.values())];

                    return searchTypeSort;
                })();
                setSearchTypeList(searchTypeList);
            }
        })();
    }, [location.pathname, location.search, location.hash]);

    const onSearchItemClick = useCallback(
        (item: ISearchItem, index: number) => {
            const theTitle = filteHtml(item.title);

            if ((window as any)._hmt) {
                const urlsearch = new URLSearchParams(window.location.search.slice(1));
                const docInfo = {
                    lang: urlsearch.get('language') || 'zh', // emun: zh/en
                    version: urlsearch.get('version') || 'current'
                };
                (window as any)._hmt.push([
                    '_trackEvent',
                    '文档搜索',
                    '点击搜索列表',
                    `标题"${theTitle}",搜索词"${keyword}",语言"${docInfo.lang}",版本"${docInfo.version}"`
                ]);

                const searchIndex = (pagination.current - 1) * pagination.pageSize + index;
                const top5Str = searchIndex <= 5 ? 'Top5' : '非Top5';
                (window as any)._hmt.push([
                    '_trackEvent',
                    '文档搜索',
                    '点击搜索列表Top5',
                    `${top5Str},第${searchIndex + 1}条,标题"${theTitle}",搜索词"${keyword}",语言"${
                        docInfo.lang
                    }",版本"${docInfo.version}"`
                ]);
            }

            const urlParams = new URLSearchParams(window.location.search.slice(1));
            fetchPostSearchInfo({
                searchQuery: keyword,
                searchType: !urlParams.get('page') || urlParams.get('page') === '1' ? 3 : 4,
                resultOrder: index + 1,
                resultUrl: item.path,
                resultTitle: theTitle,
                page: urlParams.get('page') || '1',
                classification: urlParams.get('searchType') || 'all',
                version: urlParams.get('version') || '2.4'
            }).catch(() => {
                // ignore
            });
        },
        [keyword, pagination]
    );

    const onPageChange = useCallback(
        (page: number, pageSize?: number) => {
            const searchParams = new URLSearchParams(location.search.slice(1));
            fetchPostSearchInfo({
                searchQuery: keyword,
                searchType: 4,
                version: searchParams.get('version') || '2.4',
                classification: searchParams.get('searchType') || 'all',
                page: page + ''
            }).catch(() => {
                // ignore
            });
            searchParams.set('page', `${page}`);
            historyPush(`?${searchParams.toString()}`);
            if (getScrollTop() > 500) {
                const anchorDom = document.getElementsByClassName('paddle-searchall-tab')[0];
                if (anchorDom) {
                    window.scrollTo({ top: 160 });
                }
            }
        },
        [location.search, historyPush, keyword]
    );

    const onSearch = useCallback(
        (value: string) => {
            const urlParams = new URLSearchParams(window.location.search.slice(1));
            fetchPostSearchInfo({
                searchQuery: value,
                searchType: 2,
                version: urlParams.get('version') || '2.4'
            }).catch(() => {
                // ignore
            });
            const searchParams = new URLSearchParams(location.search.slice(1));
            searchParams.set('q', value);
            searchParams.delete('page');
            searchParams.delete('searchType');
            setKeyword(value);
            historyPush(`?${searchParams.toString()}`);
        },
        [historyPush, location.search, setKeyword]
    );

    const onVersionChange = useCallback(
        (value: string) => {
            const searchParams = new URLSearchParams(location.search.slice(1));
            searchParams.set('version', value);
            searchParams.delete('page');
            searchParams.delete('searchType');
            historyPush(`?${searchParams.toString()}`);
        },
        [historyPush, location.search]
    );

    return {
        keyword,
        searchResultList,
        loading,
        pagination,
        searchType,
        searchTypeList,
        onSearch,
        onVersionChange,
        onSearchTypeChange,

        onSearchItemClick,
        onPageChange
    };
};

export default useSearchAllRouterStore;
